// Copyright 1999-2026. WebPros International GmbH. All rights reserved.

import { useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslate } from '@plesk/ui-library';
import { CombinedGraphQLErrors } from '@apollo/client/errors';
import { useApolloClient, useMutation } from '@apollo/client/react';
import { Answer, CopilotContext, createLocalAnswerKey } from '../../../CopilotContext';
import { ConsumeStream, answerFromPayload, isTransportError, userErrorMessage } from '../chatStream';
import { addToolCallToast } from './toolCallToast';
import { ToolCallActions, ToolCallDecision, toolCallKey } from './ToolCallContext';
import { isAutoDecision, latestAssistantRow, mergeToolCallsIntoAnswers, stopsAfterRun, toToolCalls } from './toolCalls';
import COPILOT_TOOL_CALL_RESULT from 'queries/CopilotToolCallResult.graphql';
import DECIDE_COPILOT_TOOL_CALLS from 'queries/DecideCopilotToolCalls.graphql';
import RUN_COPILOT_TOOL_CALLS from 'queries/RunCopilotToolCalls.graphql';
import RUN_COPILOT_TOOL_CALLS_STREAM from 'queries/RunCopilotToolCallsStream.graphql';

// After a refused run (typically still running under another tab), and after a run request
// lost in transport (the server usually goes on with the run), the history is reloaded after
// these delays, to pick up the settled batch; then it is left to the admin
export const REFUSAL_RELOAD_DELAYS_MS = [5000, 15000, 45000];

type Options = {
    // Whether runs are streamed, as answers are
    useStream: boolean;
    // The chat's stream consumer, which also applies the chunks of a streamed run
    consumeStream: ConsumeStream;
    scrollSoon: () => void;
};

/**
 * Deciding on and running the tool-call batches of the conversation shown: the automatic run,
 * the recheck of a run lost in transport, the reloads after a refused run, and the context
 * value the batch cards act through.
 */
const useToolCallRuns = ({ useStream, consumeStream, scrollSoon }: Options) => {
    const translate = useTranslate('components.copilot');
    const {
        answers, setAnswers,
        chatId,
        reloadConversation,
        historyVersion,
        runInFlight, beginRun, endRun,
    } = useContext(CopilotContext);
    const client = useApolloClient();

    const [decideMutation] = useMutation(DECIDE_COPILOT_TOOL_CALLS, { fetchPolicy: 'no-cache' });
    const [runMutation] = useMutation(RUN_COPILOT_TOOL_CALLS, { fetchPolicy: 'no-cache' });

    // toolCallKey() of every call whose decide request is in flight
    const [deciding, setDeciding] = useState<ReadonlySet<string>>(new Set());
    // Batches the automatic run has been tried for since the history was last loaded
    const autoRunTriedRef = useRef<Set<string>>(new Set());
    // Batches whose last run request failed, since the history was last loaded
    const [failedRuns, setFailedRuns] = useState<ReadonlySet<string>>(new Set());
    // Batches whose run request was lost in transport, being reloaded until their
    // continuation shows up, or until they have settled if they stop
    const [checkingRuns, setCheckingRuns] = useState<ReadonlySet<string>>(new Set());
    const checkingRunsRef = useRef(checkingRuns);
    // Batches whose last recheck reload has completed, for the next render to conclude on
    const [recheckEnds, setRecheckEnds] = useState<readonly string[]>([]);
    // Batches that have been rechecked, each only once; kept across reloads, which is what
    // bounds a reload - auto-run - failure loop
    const recheckedRef = useRef<Set<string>>(new Set());
    // How many delayed reloads each refused batch has had; kept across reloads, which is
    // what bounds a reload - auto-run - refusal loop
    const refusalReloadsRef = useRef<Map<string, number>>(new Map());
    const timersRef = useRef<Set<ReturnType<typeof setTimeout>>>(new Set());
    const chatIdRef = useRef(chatId);
    useLayoutEffect(() => {
        chatIdRef.current = chatId;
    });

    useEffect(() => {
        const timers = timersRef.current;
        return () => timers.forEach(timer => clearTimeout(timer));
    }, []);

    const setRunError = (messageId: string, error: string) => {
        setAnswers(prev => prev.map(answer => (
            answer.id === messageId && answer.role === 'assistant' ? { ...answer, runError: error } : answer
        )));
    };

    const setChecking = (messageId: string, checking: boolean) => {
        if (checkingRunsRef.current.has(messageId) === checking) {
            return;
        }
        const next = new Set(checkingRunsRef.current);
        if (checking) {
            next.add(messageId);
        } else {
            next.delete(messageId);
        }
        checkingRunsRef.current = next;
        setCheckingRuns(next);
    };

    /**
     * The server usually goes on with a run whose request was lost in transport, and stores
     * the continuation once the last call has settled. So the history is reloaded right away
     * and after each delay until a row newer than the batch shows up - or, for a batch that
     * stops, which never gets one, until every call has settled - with sending, Run and the
     * automatic run held back meanwhile. False when the batch has had its recheck.
     */
    const recheckRun = (messageId: string, runChatId: string): boolean => {
        if (checkingRunsRef.current.has(messageId)) {
            return true;
        }
        if (recheckedRef.current.has(messageId)) {
            return false;
        }
        recheckedRef.current.add(messageId);
        setChecking(messageId, true);
        const reloadAt = (attempt: number) => {
            if (chatIdRef.current !== runChatId || !checkingRunsRef.current.has(messageId)) {
                setChecking(messageId, false);
                return;
            }
            const last = attempt >= REFUSAL_RELOAD_DELAYS_MS.length;
            reloadConversation().finally(() => {
                if (last) {
                    setRecheckEnds(prev => [...prev, messageId]);
                }
            });
            if (!last) {
                const timer = setTimeout(() => {
                    timersRef.current.delete(timer);
                    reloadAt(attempt + 1);
                }, REFUSAL_RELOAD_DELAYS_MS[attempt]);
                timersRef.current.add(timer);
            }
        };
        reloadAt(0);
        return true;
    };

    // A run request that failed leaves the batch to Run, also an all-automatic one
    const runErrored = (messageId: string, intent: 'warning' | 'danger', message: string) => {
        setFailedRuns(prev => new Set(prev).add(messageId));
        addToolCallToast(intent, message);
    };

    // A refusal the server answered with, or else the generic failure
    const runRejected = (messageId: string, refusal: string | null) => {
        if (refusal === null) {
            runErrored(messageId, 'danger', String(translate('tool-call.error.generic')));
        } else {
            runErrored(messageId, 'warning', refusal);
        }
    };

    // Unless it was lost in transport and a recheck finds out how the run went
    const runFailed = (messageId: string, runChatId: string) => {
        if (!recheckRun(messageId, runChatId)) {
            runErrored(messageId, 'danger', String(translate('tool-call.error.connection-lost')));
        }
    };

    const refuseRun = (messageId: string, runChatId: string, error: string) => {
        setRunError(messageId, error);
        const attempt = refusalReloadsRef.current.get(messageId) ?? 0;
        if (attempt >= REFUSAL_RELOAD_DELAYS_MS.length) {
            return;
        }
        refusalReloadsRef.current.set(messageId, attempt + 1);
        const timer = setTimeout(() => {
            timersRef.current.delete(timer);
            if (chatIdRef.current === runChatId) {
                reloadConversation();
            }
        }, REFUSAL_RELOAD_DELAYS_MS[attempt]);
        timersRef.current.add(timer);
    };

    const runToolCalls = async (messageId: string, runChatId: string | undefined) => {
        // A second trigger while a run of the batch is in flight sends nothing
        if (!runChatId || !beginRun(messageId)) {
            return;
        }
        try {
            if (useStream) {
                const outcome = await consumeStream(
                    RUN_COPILOT_TOOL_CALLS_STREAM,
                    { input: { chatId: runChatId, messageId, stream: true } },
                    'runCopilotToolCalls',
                    {
                        focusInput: false,
                        chatGuard: runChatId,
                        onInitial: initial => {
                            // The batch as it stood before the run: a no-op merged monotonically
                            setAnswers(prev => mergeToolCallsIntoAnswers(prev, toToolCalls(initial.ranToolCalls) ?? []));
                            if (initial.runError) {
                                refuseRun(messageId, runChatId, initial.runError);
                            }
                        },
                    },
                );
                if (outcome.failed && outcome.initialError === null) {
                    if (outcome.lostInTransport) {
                        runFailed(messageId, runChatId);
                    } else {
                        runRejected(messageId, outcome.refusal);
                    }
                } else if (outcome.errorChunk) {
                    // The server ended the run with an error chunk, shown as an answer: what
                    // is left of the batch is for Run - also for an all-automatic one, which
                    // is otherwise only retried by a history reload
                    setFailedRuns(prev => new Set(prev).add(messageId));
                }
            } else {
                const result = await runMutation({ variables: { input: { chatId: runChatId, messageId } } });
                const payload = result.data?.runCopilotToolCalls;
                if (!payload) {
                    runRejected(messageId, null);
                    return;
                }
                if (chatIdRef.current !== runChatId) {
                    // Another conversation is shown now
                    return;
                }
                const key = createLocalAnswerKey();
                setAnswers(prev => {
                    const merged = mergeToolCallsIntoAnswers(prev, toToolCalls(payload.ranToolCalls) ?? []);
                    // content is null - never so for an answer - when the run settled nothing,
                    // was refused, or settled a batch that stops: nothing to continue
                    return payload.content !== null ? [...merged, answerFromPayload(payload, key, chatId)] : merged;
                });
                if (payload.runError) {
                    refuseRun(messageId, runChatId, payload.runError);
                }
                scrollSoon();
            }
        } catch (e) {
            if (isTransportError(e)) {
                runFailed(messageId, runChatId);
            } else {
                runRejected(messageId, CombinedGraphQLErrors.is(e) ? userErrorMessage(e.errors) : null);
            }
        } finally {
            endRun(messageId);
        }
    };

    const decideToolCalls = async (answer: Answer, decisions: ToolCallDecision[]) => {
        const messageId = answer.id;
        const decideChatId = answer.chat_id ?? chatId;
        if (messageId === null || !decideChatId || decisions.length === 0) {
            return;
        }
        const keys = decisions.map(({ toolCallId }) => toolCallKey({ messageId, id: toolCallId }));
        setDeciding(prev => new Set([...prev, ...keys]));
        let runNow = false;
        try {
            const result = await decideMutation({ variables: { input: { chatId: decideChatId, messageId, decisions } } });
            const payload = result.data?.decideCopilotToolCalls;
            if (payload) {
                // The whole batch, refreshed - also when the decisions were refused
                setAnswers(prev => mergeToolCallsIntoAnswers(prev, toToolCalls(payload.toolCalls) ?? []));
                if (payload.error) {
                    // Not retried: the batch now shown is not the one that was clicked on
                    addToolCallToast('warning', payload.error);
                } else {
                    runNow = payload.allDecided;
                }
            }
        } catch {
            addToolCallToast('danger', String(translate('tool-call.error.generic')));
        } finally {
            setDeciding(prev => new Set([...prev].filter(key => !keys.includes(key))));
        }
        // Not when another conversation was opened meanwhile
        if (runNow && chatIdRef.current === decideChatId) {
            runToolCalls(messageId, decideChatId);
        }
    };

    const fetchToolCallResult: ToolCallActions['fetchResult'] = async (answer, call) => {
        const result = await client.query({
            query: COPILOT_TOOL_CALL_RESULT,
            variables: { chatId: answer.chat_id ?? chatId ?? '', messageId: call.messageId, toolCallId: call.id },
            fetchPolicy: 'no-cache',
        });
        const payload = result.data?.viewer?.copilotConversationHistory?.nodes[0]?.message?.toolCall;
        if (!payload) {
            throw new Error('No tool call');
        }
        return { result: payload.result, modelResult: payload.modelResult };
    };

    const latestRow = latestAssistantRow(answers);
    // Telling the admin to run the batch only makes sense while Run is there to press: not
    // while it runs, nor for an all-automatic batch, which runs by itself unless its run failed
    const latestCalls = latestRow?.toolCalls ?? [];
    const runOffered = latestRow?.id != null
        && !runInFlight.has(latestRow.id)
        && !checkingRuns.has(latestRow.id)
        && !latestRow.runError
        && (!latestCalls.every(call => isAutoDecision(call.decision)) || failedRuns.has(latestRow.id));

    // A history load starts over: the batches found there are looked at afresh
    useEffect(() => {
        autoRunTriedRef.current = new Set();
        setFailedRuns(new Set());
    }, [historyVersion]);

    // A recheck is over once a row newer than its batch - the continuation - has shown up, the
    // run was refused, the batch is no longer shown, or it has settled and stops. Also looked
    // at when a recheck starts: the calls may have settled before the request was lost.
    useEffect(() => {
        const latestId = latestAssistantRow(answers)?.id;
        checkingRuns.forEach(id => {
            const row = answers.find(answer => answer.id === id && answer.role === 'assistant');
            const calls = row?.toolCalls ?? [];
            if (!row
                || row.runError
                || latestId !== id
                || (calls.every(call => call.execution !== null) && stopsAfterRun(calls))
            ) {
                setChecking(id, false);
            }
        });
    }, [answers, checkingRuns]);

    // After its last reload a recheck is over anyway; a batch still not run through by then is
    // left to Run
    useEffect(() => {
        if (recheckEnds.length === 0) {
            return;
        }
        recheckEnds.forEach(id => {
            if (!checkingRunsRef.current.has(id)) {
                return;
            }
            setChecking(id, false);
            const row = answers.find(answer => answer.id === id && answer.role === 'assistant');
            if (row?.toolCalls?.some(call => call.execution === null)) {
                runErrored(id, 'danger', String(translate('tool-call.error.connection-lost')));
            }
        });
        setRecheckEnds([]);
    }, [recheckEnds, answers, translate]);

    // A batch whose every call was decided automatically runs without the admin: when it
    // arrives, and on reopen, also when an earlier run died part-way. One containing a
    // decision of the admin waits for Run instead - consent to run then is not consent to
    // run whenever the tab next opens.
    useEffect(() => {
        const id = latestRow?.id;
        const calls = latestRow?.toolCalls ?? [];
        const runChatId = latestRow?.chat_id ?? chatId;
        if (!id
            || !runChatId
            || latestRow?.runError
            || calls.length === 0
            || !calls.every(call => isAutoDecision(call.decision))
            || !calls.some(call => call.execution === null)
            || runInFlight.has(id)
            || checkingRuns.has(id)
            || autoRunTriedRef.current.has(id)
        ) {
            return;
        }
        autoRunTriedRef.current.add(id);
        runToolCalls(id, runChatId);
    });

    // The handlers close over the current render's state; the context value stays stable
    const handlersRef = useRef({ decideToolCalls, runToolCalls, fetchToolCallResult, chatId });
    useLayoutEffect(() => {
        handlersRef.current = { decideToolCalls, runToolCalls, fetchToolCallResult, chatId };
    });
    const latestRowId = latestRow?.id ?? null;
    const lastAnswerKey = answers[answers.length - 1]?.key ?? null;
    const toolCallActions = useMemo<ToolCallActions>(() => ({
        decide: (answer, decisions) => {
            handlersRef.current.decideToolCalls(answer, decisions);
        },
        run: answer => {
            if (answer.id !== null) {
                handlersRef.current.runToolCalls(answer.id, answer.chat_id ?? handlersRef.current.chatId);
            }
        },
        fetchResult: (answer, call) => handlersRef.current.fetchToolCallResult(answer, call),
        deciding,
        runInFlight,
        failedRuns,
        checkingRuns,
        latestRowId,
        lastAnswerKey,
    }), [deciding, runInFlight, failedRuns, checkingRuns, latestRowId, lastAnswerKey]);

    return {
        toolCallActions,
        // Whether a decide request for a call of the latest batch is in flight
        latestDeciding: latestCalls.some(call => deciding.has(toolCallKey(call))),
        // Whether the latest batch is being run from this tab
        latestRunInFlight: latestRow?.id != null && runInFlight.has(latestRow.id),
        // Whether a recheck may yet bring in the continuation of the latest batch
        latestRechecking: latestRow?.id != null && checkingRuns.has(latestRow.id),
        latestRunOffered: runOffered,
    };
};

export default useToolCallRuns;
